// `Securite` settings section — US-008..US-014.
//
// Composes:
//   - Change-password form (US-008) with `PasswordStrengthMeter` reuse
//     from W4.2 + zxcvbn-driven submit gate.
//   - 2FA enroll/disable dialog (US-009, US-010).
//   - Active sessions list with revoke (US-011, US-012, US-013).
//   - Type-email-to-confirm delete-account dialog (US-014).
//
// All flows route through the existing api-client hooks (no inline
// fetch). The change-password form lives inline (vs a separate dialog)
// to mirror the proto's "ghost" CTA inside the row — the row expands
// in place.

import { useState } from 'react';

import {
  ApiError,
  useChangePassword,
  useMe,
  useRevokeOtherSessions,
  useRevokeSession,
  useSessions,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Checkbox, Field, IconButton, Input, toast } from '@barel/ui';
import { Eye, EyeOff } from 'lucide-react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { PasswordStrengthMeter } from '@/features/auth/components/PasswordStrengthMeter';
import { MIN_PASSWORD_SCORE, usePasswordStrength } from '@/features/auth/lib/usePasswordStrength';
import { confirm } from '@/features/shell/GlobalConfirmModal';

import { DeleteAccountDialog } from '../components/DeleteAccountDialog';
import { SessionRow } from '../components/SessionRow';
import { TwoFactorSetup } from '../components/TwoFactorSetup';

const PASSWORD_SCHEMA = z
  .object({
    currentPassword: z.string().min(1).max(256),
    newPassword: z.string().min(10).max(256),
    confirmPassword: z.string().min(1).max(256),
    revokeOtherSessions: z.boolean().optional(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'mismatch',
  });

type PasswordValues = z.infer<typeof PASSWORD_SCHEMA>;

export function Securite() {
  const t = useT('settings');
  const me = useMe();
  const sessions = useSessions({ enabled: true });
  const changePassword = useChangePassword();
  const revoke = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();
  const user = me.data?.user;
  const sessionId = me.data?.session?.id;
  const otherSessionsCount = (sessions.data?.sessions ?? []).filter(
    (s) => s.id !== sessionId,
  ).length;

  const [expanded, setExpanded] = useState<boolean>(false);
  const [showCurrent, setShowCurrent] = useState<boolean>(false);
  const [showNew, setShowNew] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<PasswordValues>({
    resolver: zodResolver(PASSWORD_SCHEMA),
    mode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      revokeOtherSessions: false,
    },
  });
  const newPassword = form.watch('newPassword');
  const strength = usePasswordStrength(newPassword);
  const submitDisabled =
    !newPassword || strength.score === null || strength.score < MIN_PASSWORD_SCORE;

  const onSubmit: SubmitHandler<PasswordValues> = async (values) => {
    setSubmitError(null);
    if (strength.score === null || strength.score < MIN_PASSWORD_SCORE) {
      setSubmitError(t('security.passwordWeakError'));
      return;
    }
    try {
      await changePassword.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        revokeOtherSessions: values.revokeOtherSessions === true,
      });
      toast.success({ title: t('security.passwordSuccess') });
      setExpanded(false);
      form.reset({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        revokeOtherSessions: false,
      });
    } catch (err) {
      if (ApiError.is(err) && (err.code === 'auth.bad_credentials' || err.httpStatus === 400)) {
        setSubmitError(t('security.passwordError'));
      } else {
        setSubmitError(t('profile.saveError'));
      }
    }
  };

  return (
    <section
      data-section="security"
      aria-labelledby="settings-security-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1 border-b border-default pb-4">
        <h1 id="settings-security-title" className="text-lg font-semibold text-primary">
          {t('security.title')}
        </h1>
        <p className="text-xs text-tertiary">{t('security.subtitle')}</p>
      </header>

      {/* Change password */}
      <div className="flex flex-col gap-3 border-b border-subtle pb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-sm font-medium text-primary">{t('security.passwordTitle')}</span>
            <span className="text-xs text-tertiary">{t('security.passwordHint')}</span>
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((v) => !v)}
            data-testid="settings-password-toggle"
          >
            {t('security.passwordChange')}
          </Button>
        </div>
        {expanded ? (
          <form
            onSubmit={(event) => {
              void form.handleSubmit(onSubmit)(event);
            }}
            className="flex flex-col gap-3 rounded-lg border border-default bg-bg-elevated p-4"
            data-testid="settings-password-form"
            noValidate
          >
            {submitError ? <Banner tone="error">{submitError}</Banner> : null}

            <Field required invalid={!!form.formState.errors.currentPassword}>
              <Field.Label>{t('security.passwordCurrentLabel')}</Field.Label>
              <Field.Control>
                <Input
                  {...form.register('currentPassword')}
                  type={showCurrent ? 'text' : 'password'}
                  autoComplete="current-password"
                  data-testid="settings-password-current"
                  iconRight={
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={
                        showCurrent
                          ? t('security.passwordCurrentLabel')
                          : t('security.passwordCurrentLabel')
                      }
                      icon={showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                      onClick={() => setShowCurrent((v) => !v)}
                    />
                  }
                />
              </Field.Control>
            </Field>

            <Field required invalid={!!form.formState.errors.newPassword}>
              <Field.Label>{t('security.passwordNewLabel')}</Field.Label>
              <Field.Control>
                <Input
                  {...form.register('newPassword')}
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  data-testid="settings-password-new"
                  iconRight={
                    <IconButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('security.passwordNewLabel')}
                      icon={showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                      onClick={() => setShowNew((v) => !v)}
                    />
                  }
                />
              </Field.Control>
              <PasswordStrengthMeter score={strength.score} ready={strength.ready} />
            </Field>

            <Field required invalid={!!form.formState.errors.confirmPassword}>
              <Field.Label>{t('security.passwordConfirmLabel')}</Field.Label>
              <Field.Control>
                <Input
                  {...form.register('confirmPassword')}
                  type={showNew ? 'text' : 'password'}
                  autoComplete="new-password"
                  data-testid="settings-password-confirm"
                />
              </Field.Control>
              {form.formState.errors.confirmPassword ? (
                <Field.Error>{t('security.passwordMismatchError')}</Field.Error>
              ) : null}
            </Field>

            <label className="inline-flex items-center gap-2 text-sm text-secondary">
              <Checkbox
                checked={form.watch('revokeOtherSessions')}
                onCheckedChange={(value: boolean | 'indeterminate') =>
                  form.setValue('revokeOtherSessions', value === true)
                }
                data-testid="settings-password-revoke-others"
              />
              <span>{t('security.passwordRevokeOthersLabel')}</span>
            </label>

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                size="sm"
                variant="primary"
                loading={form.formState.isSubmitting || changePassword.isPending}
                disabled={submitDisabled}
                data-testid="settings-password-submit"
              >
                {t('security.passwordSubmit')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setExpanded(false);
                  setSubmitError(null);
                  form.reset();
                }}
              >
                {/* Cancel uses common namespace */}
                {t('security.deleteCancel')}
              </Button>
            </div>
          </form>
        ) : null}
      </div>

      {/* 2FA */}
      <div className="flex items-start justify-between gap-4 border-b border-subtle pb-5">
        <div className="flex flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-primary">{t('security.twoFactorTitle')}</span>
          <span className="text-xs text-tertiary">{t('security.twoFactorHint')}</span>
          {user?.twoFactorEnabled ? (
            <span
              data-testid="settings-2fa-enabled-badge"
              className="mt-1 inline-flex items-center gap-1 self-start rounded-full border border-accent-border bg-accent-dim px-2 py-0.5 text-2xs font-semibold uppercase tracking-wider text-accent"
            >
              {t('security.twoFactorEnabledBadge')}
            </span>
          ) : null}
        </div>
        <TwoFactorSetup enabled={user?.twoFactorEnabled === true} />
      </div>

      {/* Sessions */}
      <div className="flex flex-col gap-3 border-b border-subtle pb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-1 flex-col gap-0.5">
            <span className="text-sm font-medium text-primary">{t('security.sessionsTitle')}</span>
            <span className="text-xs text-tertiary">{t('security.sessionsHint')}</span>
          </div>
          {otherSessionsCount > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              loading={revokeOthers.isPending}
              data-testid="settings-sessions-revoke-all"
              onClick={async () => {
                const ok = await confirm({
                  title: t('security.sessionRevokeOthers'),
                  body: t('security.sessionRevokeOthersConfirm'),
                  tone: 'destructive',
                  confirmLabel: t('security.sessionRevokeOthers'),
                });
                if (!ok) return;
                try {
                  await revokeOthers.mutateAsync();
                  toast.success({ title: t('security.sessionRevokeOthersToast') });
                } catch {
                  toast.error({ title: t('profile.saveError') });
                }
              }}
            >
              {t('security.sessionRevokeOthers')}
            </Button>
          ) : null}
        </div>
        {sessions.isLoading ? (
          <div className="text-xs text-tertiary">{t('profile.avatarUploading')}</div>
        ) : (
          <ul
            className="overflow-hidden rounded-lg border border-default bg-bg-surface"
            data-testid="settings-sessions"
          >
            {(sessions.data?.sessions ?? []).map((session) => (
              <SessionRow
                key={session.id}
                session={session}
                current={sessionId === session.id}
                busy={revoke.isPending && revoke.variables?.token === session.token}
                onRevoke={async (token: string) => {
                  try {
                    await revoke.mutateAsync({ token });
                    toast.success({ title: t('security.sessionRevokedToast') });
                  } catch {
                    toast.error({ title: t('profile.saveError') });
                  }
                }}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Danger zone */}
      <div className="flex flex-col gap-3 rounded-lg border border-error-border bg-error-dim p-5">
        <div className="flex flex-col gap-1">
          <h2 className="text-sm font-semibold text-error">{t('security.deleteAccountTitle')}</h2>
          <p className="text-xs text-secondary">{t('security.deleteAccountHint')}</p>
        </div>
        <DeleteAccountDialog email={user?.email ?? ''} />
      </div>
    </section>
  );
}
